import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectIdentifyAt, selectQueryableLayers } from '../../store/selectors/index.js';
import { identifyDismissed } from '../../store/slices/mapSlice.js';
import { useIdentifyQuery } from '../../services/identifyApi.js';
import IdentifyResultCard from './IdentifyResultCard.js';

/**
 * Feature attributes for the last map click.
 *
 * Rendered as a panel rather than the SDK's own popup so it inherits the
 * application's theme and focus handling, and so long attribute tables scroll
 * predictably instead of overflowing a balloon anchored to a point.
 *
 * Only visible, queryable layers are asked. Querying a hidden layer would
 * return attributes for something the user cannot see, which reads as a bug.
 */
export default function IdentifyPanel() {
  const dispatch = useAppDispatch();
  const at = useAppSelector(selectIdentifyAt);
  const queryable = useAppSelector(selectQueryableLayers);

  const layerNames = queryable.map((l) => l.name);
  const titles = new Map(queryable.map((l) => [l.name, l.title]));

  const { data, isFetching } = useIdentifyQuery(
    at ? { layers: layerNames, point: at.point, resolution: at.resolution } : skip,
    { skip: !at || layerNames.length === 0 },
  );

  if (!at) return null;

  const nothingQueryable = layerNames.length === 0;
  const results = data ?? [];

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label="Feature information"
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        width: 340,
        maxWidth: 'calc(100% - 24px)',
        maxHeight: 'calc(100% - 96px)',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h2" sx={{ fontSize: '0.875rem' }}>
            {nothingQueryable
              ? 'Nothing to identify'
              : results.length === 1
                ? '1 feature'
                : `${results.length} features`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {at.point.lat.toFixed(5)}, {at.point.lng.toFixed(5)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => dispatch(identifyDismissed())} aria-label="Close feature information">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {isFetching && <LinearProgress aria-label="Looking up features" />}

      <Box sx={{ overflow: 'auto', minHeight: 0 }}>
        {nothingQueryable && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Add a layer to the map, then click it to see its attributes.
          </Typography>
        )}

        {!nothingQueryable && !isFetching && results.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No features at this location.
          </Typography>
        )}

        {results.map((r) => (
          <IdentifyResultCard
            key={`${r.layerName}:${r.featureId}`}
            result={r}
            title={titles.get(r.layerName) ?? r.layerName}
          />
        ))}
      </Box>
    </Paper>
  );
}

/**
 * RTK Query still type-checks the argument of a skipped query, so a
 * structurally valid placeholder is needed when there is no click to report.
 */
const skip = { layers: [] as string[], point: { lng: 0, lat: 0 }, resolution: 0 };
